import type { NowNextProgramInfo, ProgramEpgData } from '@norlys-a-s/norlys-play-kmm';
import { LiveTvChannel, LiveTvProgram } from '@smart-tv/typings/content';
import { TrickModes } from '@smart-tv/components/player/types';
import getUnixDate from '@smart-tv/utils/date/get-unix-date';

import { getFavoriteListIdentifierForKmm, kmm } from './kmm';

export type GetEpgDataNowNextForChannelsParams = {
  favoriteList: number;
  nextCount?: number;
};

export interface GetEpgDataNowNextForChannelsResponse {
  updateNextInSec: number;
  channels: LiveTvChannel[];
}

const formatLiveTvProgram = (program: ProgramEpgData, sid: number): LiveTvProgram => {
  const npvrTrickmodes = program.npvrInfo?.trickModes?.bitmask ?? 0;
  const genres = Array.from(program.genre?.subGenres?.asJsReadonlyArrayView() ?? []).map(
    (subGenre) => subGenre.name
  );

  return {
    category: program.trackingCategory || '',
    duration: program.endTimeEpochSeconds - program.startTimeEpochSeconds,
    endTime: getUnixDate(program.endTimeEpochSeconds),
    eventId: program.eventId,
    genres,
    image: program.programImage || '',
    npvrTrickmodes,
    recordable: (npvrTrickmodes & TrickModes.CatchupAllow) !== 0,
    recording: program.npvrInfo?.isCustomerRecording ?? false,
    sid,
    startTime: getUnixDate(program.startTimeEpochSeconds),
    title: program.title || undefined,
    year: program.year ? parseInt(program.year, 10) : undefined
  };
};

const responseMapper = (
  epgData: ReadonlyArray<NowNextProgramInfo>,
  updateNextInSec: number
): GetEpgDataNowNextForChannelsResponse => {
  const channels = epgData.map((channel) => {
    const sid = channel.sid;
    const currentProgram = channel.now ? formatLiveTvProgram(channel.now, sid) : undefined;
    const nextProgramData = Array.from(channel.next?.asJsReadonlyArrayView() ?? [])[0];
    const nextProgram = nextProgramData ? formatLiveTvProgram(nextProgramData, sid) : undefined;

    return {
      currentProgram,
      icon: channel.channelIconImageUrl,
      name: channel.channelName,
      nextProgram,
      sid
    };
  });

  return {
    updateNextInSec,
    channels
  };
};

export const getEpgDataNowNextForChannels = async (
  params: GetEpgDataNowNextForChannelsParams
): Promise<GetEpgDataNowNextForChannelsResponse> => {
  const nextCount: number = typeof params.nextCount === 'number' ? params.nextCount : 1;

  const result = await kmm((repo) =>
    repo.getEpgDataNowNextForChannels(
      nextCount,
      getFavoriteListIdentifierForKmm(params.favoriteList)
    )
  );

  return responseMapper(Array.from(result.epgData.asJsReadonlyArrayView()), result.updateNextInSec);
};

export default getEpgDataNowNextForChannels;
